import { Avatar, Box, Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonDownload from "components/Buttons/IconButtonDownload";
import { SpinnerIndicator } from "components/CallServer";
import { exportToXLSX } from "components/ExportToExcel";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { Formaters } from "lib/Formarters";
import HtmlUtils from "lib/HtmlUtils";
import { TenantBillingType } from "models/ApiEnums";
import { DashboardConsumptionDetailColumn, DashboardConsumptionSummaryColumn, DashboardConsumptionUserListModel, DashboardGraphModel, PagedResult } from "models/ApiModels";
import { LicenseManagerEmail } from "models/AppModels";
import moment from "moment";
import * as React from "react";
import { useTranslation, WithTranslation } from "react-i18next";
import Session from "services/Session";
import { RenderBarGraph } from "views/Dashboards/components/RenderBarGraph";
import { RenderGraphGmtInfo } from "views/Dashboards/components/RenderGraphGmtInfo";
import { CommonProps } from "views/Dashboards/ConsumptionAnalysisDashboard";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphProps {
    tile: DashboardConsumptionSummaryColumn;
}

interface IGraphState {
    data: DashboardGraphModel[];
    loading: boolean;
    selectedDay?: Date;
}

type Props = IGraphProps & CommonProps;

/////////////////////////////////////////////////////////////////////////////////////

const ActiveGraphGmtInfo: React.FC<{}> = (props) => {
    const { t } = useTranslation();
    var currentDate = moment().utc();
    const now = moment();
    const dateInfo = currentDate.day() != now.day() ? `(${currentDate.format("LL")})` : "";
    const resource = `Views.Dashboards.ConsumptionAnalysis.AnalysisUsersGraph.ActiveGmtInfoText`;
    return <div>{`${t(resource)} ${currentDate.format("HH:mm")}h ${dateInfo}`}</div>;
};

export class ConsumptionAnalysisUsersGraph extends React.PureComponent<Props, IGraphState>{

    private billingType: TenantBillingType;
    private maxUsers: number;

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            selectedDay: null,
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.ConsumptionAnalysis.AnalysisUsersGraph." + r);
    }

    ///
    private getDateFilter() {
        var de = moment().utc().startOf("month");
        var ds = moment().utc().startOf("month");
        ds = ds.add(-11, "months");

        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    private loadData() {
        api.getDashboardConsumptionAnalysis({
            columnToSummary: this.props.tile,
            detailColumn: DashboardConsumptionDetailColumn.GraphByMonth,
            ...this.getDateFilter(),

        })
            .then((result) => {
                this.billingType = result.billingType;
                this.maxUsers = result.maxUsers;
                (result.data as DashboardGraphModel[]).forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                });

                this.setState({ data: (result.data as DashboardGraphModel[]), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private renderLicenseResume(): JSX.Element {
        const { data } = this.state;
        const currentLicensedUsers = data.length > 0 ? data[data.length - 1].total : 0;

        return (
            <div>
                <Box mt={2}>
                    <Typography component="p" variant="button">{this.res("LicensesContractedLabel")}: <b>{this.maxUsers}</b></Typography>
                    <MuiColor variant="warning"><Typography component="p" variant="button">{this.res("LicensesInUseLabel")}: <b>{currentLicensedUsers}</b></Typography></MuiColor>
                    <MuiColor variant="success"><Typography component="p" variant="button">{this.res("LicensesRemainingLabel")}: <b>{this.maxUsers - currentLicensedUsers}</b></Typography></MuiColor>
                </Box>
                <Box mt={4}>
                    <Typography variant="button"><b>{this.res("LicenseChangeTitle")}</b></Typography>
                    <br />
                    <MuiMuted>
                        {this.res("LicenseChangeSubtitle")}: <a href={LicenseManagerEmail}>{LicenseManagerEmail}</a>
                    </MuiMuted>
                </Box>
            </div>
        );
    }

    ///
    private handleSelectDay = (dt: Date) => this.setState({ selectedDay: dt.convertUtcToLocalDate() });

    ///
    private handleCancelDay = () => this.setState({ selectedDay: null });

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { loading, data } = this.state;

        return (
            <>
                <MuiContainer margin="dense" >
                    <small>
                        <b>
                            {
                                this.billingType == TenantBillingType.LicensedUsers
                                    ? this.res("LicensedUsersHeaderTitle").toLocaleUpperCase()
                                    : this.res("ActiveUsersHeaderTitle").toLocaleUpperCase()
                            }
                        </b>
                    </small>
                    <br />
                    <MuiMuted>
                        <small>
                            {
                                this.billingType == TenantBillingType.LicensedUsers &&
                                <>
                                    <span>{this.res("LicensedUsersSubtitle")}</span>
                                    <br />
                                    <RenderGraphGmtInfo />
                                </>
                            }

                            {
                                this.billingType == TenantBillingType.ActiveUsers &&
                                <ActiveGraphGmtInfo />
                            }
                        </small>
                    </MuiMuted>
                </MuiContainer>

                {
                    (loading) ?
                        <SpinnerIndicator />
                        : (data == null)
                            ? <AppMessageBox resource="LoadGraphDataFailed" />
                            : (
                                <>
                                    <RenderBarGraph
                                        color="orange"
                                        data={this.state.data}
                                        lastItemFade={0.6}
                                        onSelectDay={
                                            this.billingType == TenantBillingType.ActiveUsers
                                                ? this.handleSelectDay
                                                : null
                                        }
                                    />

                                    <MuiContainer>
                                        {
                                            this.billingType == TenantBillingType.ActiveUsers
                                                ? (
                                                    <ConsumptionUserList
                                                        billingType={this.billingType}
                                                        {...this.props}
                                                        {...this.getDateFilter()}
                                                        selectedDay={this.state.selectedDay}
                                                        onCancelDay={this.handleCancelDay}
                                                    />
                                                )
                                                : this.renderLicenseResume()
                                        }
                                    </MuiContainer>
                                </>
                            )
                }
            </>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<DashboardConsumptionUserListModel>;
    offset: number;
    loading: boolean;
    periodOption: number;
}

interface IListProps extends IGraphProps {
    billingType: TenantBillingType;
    startDate: Date;
    endDate: Date;
    selectedDay: Date;
    onCancelDay: () => void;
}

type UserListProps = IListProps & WithTranslation;

class ConsumptionUserList extends React.PureComponent<UserListProps, IListState> {

    constructor(props: UserListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            periodOption: null,
        };
    }

    ///
    private res(r: string, options?: any) {
        return this.props.t("Views.Dashboards.ConsumptionAnalysis.AnalysisUsersGraph." + r, options);
    }

    ///
    private getDateFilter() {
        var ds = moment(this.state.periodOption).utc().startOf("month");
        var de = moment(this.state.periodOption).utc().endOf("month").subtract("seconds", 1);
        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardConsumptionAnalysis(
            {
                columnToSummary: this.props.tile,
                detailColumn: DashboardConsumptionDetailColumn.ActiveUsers,
                ...this.getDateFilter(),
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
            })
            .then((result) => {
                (result.data.items as DashboardConsumptionUserListModel[]).forEach((v) => {
                    if (v.lastLogin) v.lastLogin = v.lastLogin.convertLocalToUtcDate();
                });

                this.setState({
                    data: result.data,
                    loading: false,
                    offset: offset
                }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private handlePeriodOption = (e: SelectChangeEvent<number>) => {
        this.setState({ periodOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getPeriodOptions() {
        var result: Array<{ key: Date, text: string; }> = [];

        const startDay = moment(this.props.startDate);
        var dt = moment(this.props.endDate).utc();

        while (dt >= startDay) {
            result.push({
                key: dt.toDate(),
                text: moment(dt).format("MMM/YYYY")
            });

            dt = dt.subtract(1, "month").startOf("month");
        }

        return result;
    }

    ///
    private renderHeader() {
        const dateRange = this.getDateFilter();

        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="flex-start">

                        <Grid item>
                            <b>
                                {this.res("ActiveUsersDetail").toLocaleUpperCase()}
                            </b>
                            <br />
                            <MuiMuted>
                                <small>
                                    {
                                        this.res("PeriodText",
                                            {
                                                startDate: Formaters.localeDatetime(dateRange.startDate),
                                                endDate: Formaters.localeDatetime(dateRange.endDate)
                                            }
                                        )
                                    }
                                    <br />
                                    <span>{this.res("TotalUsers", { count: this.state.data.total })}</span>
                                </small>
                            </MuiMuted>
                        </Grid>

                        <Grid item>

                            <Select
                                value={this.state.periodOption}
                                onChange={this.handlePeriodOption}
                            >
                                {
                                    this.getPeriodOptions().map((v, index) => <MenuItem key={index} value={v.key.getTime()}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>
                    </Grid>
                </MuiContainer>
            </div >
        );
    }

    ///
    private renderListBody() {
        const { data: { items }, offset } = this.state;

        if (items.length == 0)
            return null;

        return (
            <Table size="small">
                <TableHead>
                    <TableCell colSpan={2}>&nbsp;</TableCell>
                    <TableCell align="right">
                        <span>{this.res("LastAccessText")}</span>
                    </TableCell>
                </TableHead>
                <TableBody>
                    {
                        items.map((v, index): JSX.Element => {
                            return (
                                <TableRow key={index}>

                                    <TableCell style={{ width: 1 }}>
                                        <Avatar src={v.thumbnailUrl} />
                                    </TableCell>

                                    <TableCell>
                                        <span>{v.name}</span>
                                    </TableCell>

                                    <TableCell align="right">
                                        {
                                            v.lastLogin &&
                                            <div>
                                                <div>{Formaters.dayMonth(v.lastLogin)} {Formaters.localeTime(v.lastLogin)}</div>
                                                {
                                                    v.lastLoginIpAddress &&
                                                    <div><MuiMuted>IP: {v.lastLoginIpAddress}</MuiMuted></div>
                                                }
                                            </div>
                                        }

                                    </TableCell>
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        );
    }

    ///
    private renderList() {
        const { data: { total }, dialog } = this.state;

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        total == 0
                            ? <MuiMessageBox variant="warning" message={this.res("EmptyData")} />
                            : (
                                <>
                                    <MuiContainer>
                                        {this.renderListBody()}
                                    </MuiContainer>
                                    {
                                        total > 0 &&
                                        <Grid container justifyContent="center" alignItems="center">
                                            <Grid item xs>
                                                <MuiPagination
                                                    limit={DefaultListParameters.viewRecordsByPage}
                                                    offset={this.state.offset}
                                                    total={total}
                                                    onClick={(e, offset) => this.loadData(offset, true)}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <IconButtonDownload onClick={this.handleExport} />
                                            </Grid>
                                        </Grid>
                                    }
                                </>
                            )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    private updateListOption() {
        const periodOption = this.props.selectedDay == null
            ? this.props.endDate.getTime()
            : this.props.selectedDay.getTime();

        this.setState({ periodOption }, () => this.loadData(0));
    }

    ///
    private handleExport = () => {

        const dateFilter = this.getDateFilter();

        api.getDashboardConsumptionAnalysis(
            {
                columnToSummary: this.props.tile,
                detailColumn: DashboardConsumptionDetailColumn.ActiveUsers,
                ...dateFilter,
                range: { offset: 0, take: 32767 },
            })
            .then((result) => {
                const fileName = 'UsersByMonth-' + dateFilter.startDate.convertLocalToUtcDate().toLocaleDateString(Session.user.culture);
                const csv = (result.data.items as DashboardConsumptionUserListModel[]).map((v) => {
                    return { name: v.name, lastLogin: v.lastLogin, lastLoginIpAddress: v.lastLoginIpAddress };
                });
                if (csv.length > 0) exportToXLSX(csv, fileName);
            });
    };

    ///
    private checkForUpdateState(prevProps: UserListProps) {
        if (
            (this.props.selectedDay != null && this.props.selectedDay != prevProps.selectedDay)
            ||
            (this.props.selectedDay != null && prevProps.selectedDay == null)
            ||
            (this.props.selectedDay == null && prevProps.selectedDay != null)
        ) {
            this.updateListOption();
        }
    }

    ///
    public componentDidUpdate(prevProps: UserListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public componentDidMount() {
        this.updateListOption();
    }

    ///
    public render() {

        if (this.props.startDate == null)
            return null;

        return (
            <div>
                {
                    (this.state.data == null && !this.state.loading)
                        ? <AppMessageBox resource="LoadListDataFailed" />
                        : this.state.loading
                            ? <SpinnerIndicator />
                            : this.renderList()
                }
            </div>
        );
    }
}